/**
 * Order compensation & adjustments — approve partner recovery, manage installment plans.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from "@mui/material";
import { Add, ArrowBack } from "@mui/icons-material";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { getInvoices } from "../../invoices/services/invoiceService.js";
import AdjustmentCard from "../components/AdjustmentCard.jsx";
import AdjustmentApproveModal from "../components/AdjustmentApproveModal.jsx";
import AdjustmentForm from "../components/AdjustmentForm.jsx";
import CompensationPlanCard from "../components/CompensationPlanCard.jsx";
import CompensationPlanForm from "../components/CompensationPlanForm.jsx";
import {
  getAdjustmentsByJobId,
  createAdjustment,
  approveAdjustment,
  rejectAdjustment,
} from "../services/adjustmentService.js";
import {
  getCompensationPlansByJobId,
  createCompensationPlan,
} from "../services/compensationService.js";
import { getOrder } from "../../orders/services/orderService.js";
import { getDriver } from "../../drivers/services/driverService.js";
import { getPayeeLabel } from "../utils/resolvePayeeLabel.js";
import { PAGE, BANNER, TABS } from "../copy/opsDescriptions.js";

const normalizeInvoices = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.invoices)) return payload.invoices;
  return [];
};

export default function OrderCompensationView() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.userId || user?.id || user?.email || "system";

  const [tab, setTab] = useState(0);
  const [adjustments, setAdjustments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [order, setOrder] = useState(null);
  const [payeeNamesById, setPayeeNamesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [adjFormOpen, setAdjFormOpen] = useState(false);
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [approveModalAdj, setApproveModalAdj] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const [adjRes, planRes, invRes, orderData] = await Promise.all([
        getAdjustmentsByJobId(orderId),
        getCompensationPlansByJobId(orderId),
        getInvoices({ jobId: orderId, limit: 50 }),
        getOrder(orderId).catch(() => null),
      ]);
      if (!adjRes.success) throw new Error(adjRes.error);
      if (!planRes.success) throw new Error(planRes.error);
      const adjList = adjRes.data || [];
      setAdjustments(adjList);
      setPlans(planRes.data || []);
      setInvoices(invRes?.success ? normalizeInvoices(invRes.data) : []);
      setOrder(orderData || null);

      const names = {};
      const idsToFetch = new Set();
      for (const adj of adjList) {
        if (!adj.providerId) continue;
        const label = getPayeeLabel(adj, orderData) || adj.providerName;
        if (label) names[adj.providerId] = label;
        else idsToFetch.add(adj.providerId);
      }
      await Promise.all(
        [...idsToFetch].map(async (id) => {
          try {
            const res = await getDriver(id);
            const driver = res?.data || res;
            if (driver?.name) names[id] = driver.name;
          } catch {
            // ignore
          }
        })
      );
      setPayeeNamesById(names);
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateAdjustment = async (data) => {
    setActionLoading(true);
    const res = await createAdjustment(orderId, { ...data, createdBy: actorId });
    setActionLoading(false);
    if (res.success) {
      notify("Adjustment created — approve to recover from partner on payout");
      setAdjFormOpen(false);
      load();
    } else {
      notify(res.error, "error");
    }
  };

  const handleApproveClick = (adj) => {
    if (["PROVIDER", "SHARED"].includes(adj.responsibility)) {
      setApproveModalAdj(adj);
      return;
    }
    handleApproveBubakiiOnly(adj);
  };

  const handleApproveBubakiiOnly = async (adj) => {
    setActionLoading(true);
    const res = await approveAdjustment(orderId, adj.id, actorId);
    setActionLoading(false);
    if (res.success) {
      notify("Approved");
      load();
    } else {
      notify(res.error, "error");
    }
  };

  const handleApprovedWithProviderEmail = () => {
    notify("Approved — partner notified and recovery will apply on payout (capped per run)");
    load();
  };

  const handleReject = async (adj) => {
    setActionLoading(true);
    const res = await rejectAdjustment(orderId, adj.id, actorId);
    setActionLoading(false);
    if (res.success) {
      notify("Adjustment rejected");
      load();
    } else {
      notify(res.error, "error");
    }
  };

  const handleCreatePlan = async (data) => {
    setActionLoading(true);
    const res = await createCompensationPlan(orderId, { ...data, createdBy: actorId });
    setActionLoading(false);
    if (res.success) {
      const msg = data.createProviderAdjustment
        ? "Compensation plan created. Approve the linked adjustment under the Adjustments tab."
        : "Compensation plan created";
      notify(msg);
      setPlanFormOpen(false);
      setTab(data.createProviderAdjustment ? 0 : 1);
      load();
    } else {
      notify(res.error, "error");
    }
  };

  const handlePayInstallmentComplete = () => {
    notify("Installment paid and confirmation email sent to customer");
    load();
  };

  const pendingCount = adjustments.filter((a) => a.status === "pending").length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(`/orders/${orderId}`)} sx={{ textTransform: "none" }}>
            Back to order
          </Button>
          <Box>
            <Typography variant="h4" component="h1">
              {PAGE.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
              {PAGE.subtitle}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {tab === 0 && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAdjFormOpen(true)} sx={{ textTransform: "none" }}>
              New adjustment
            </Button>
          )}
          {tab === 1 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setPlanFormOpen(true)}
              disabled={invoices.length === 0}
              sx={{ textTransform: "none" }}
            >
              New compensation plan
            </Button>
          )}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {BANNER.summary} {BANNER.recoveryNote}
        {pendingCount > 0 && ` You have ${pendingCount} adjustment(s) awaiting approval.`}
      </Alert>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`${TABS.adjustments.label} (${adjustments.length})`} />
        <Tab label={`${TABS.plans.label} (${plans.length})`} />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tab === 0 ? TABS.adjustments.description : TABS.plans.description}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 &&
            (adjustments.length === 0 ? (
              <Typography color="text.secondary">{TABS.adjustments.empty}</Typography>
            ) : (
              adjustments.map((adj) => (
                <AdjustmentCard
                  key={adj.id}
                  adjustment={adj}
                  payeeLabel={
                    payeeNamesById[adj.providerId] ||
                    getPayeeLabel(adj, order) ||
                    adj.providerName
                  }
                  onApprove={handleApproveClick}
                  onReject={handleReject}
                  actionLoading={actionLoading}
                />
              ))
            ))}

          {tab === 1 &&
            (plans.length === 0 ? (
              <Typography color="text.secondary">
                {invoices.length === 0 ? TABS.plans.emptyNoInvoice : TABS.plans.empty}
              </Typography>
            ) : (
              plans.map((plan) => (
                <CompensationPlanCard
                  key={plan.id}
                  plan={plan}
                  jobId={orderId}
                  processedBy={actorId}
                  onPayInstallment={handlePayInstallmentComplete}
                  payLoading={actionLoading}
                />
              ))
            ))}
        </>
      )}

      <AdjustmentForm open={adjFormOpen} onClose={() => setAdjFormOpen(false)} onSubmit={handleCreateAdjustment} loading={actionLoading} />
      <CompensationPlanForm
        open={planFormOpen}
        onClose={() => setPlanFormOpen(false)}
        onSubmit={handleCreatePlan}
        loading={actionLoading}
        invoices={invoices}
      />

      <AdjustmentApproveModal
        open={!!approveModalAdj}
        onClose={() => setApproveModalAdj(null)}
        jobId={orderId}
        adjustment={approveModalAdj}
        approvedBy={actorId}
        onApproved={handleApprovedWithProviderEmail}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification((p) => ({ ...p, open: false }))}
        message={notification.message}
      />
    </Container>
  );
}
